import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './Alerts.css';
import { useAlerts } from '../contexts/AlertsContext';
import { useToast } from '../contexts/ToastContext';
import alertsService from '../services/alertsService';
import { getDirections } from '../services/directionsService';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function Alerts() {
  const { myAlerts, nearbyAlerts, deleteAlert, refreshAlerts } = useAlerts();
  const { addToast } = useToast();
  const navigate = useNavigate();

  useEffect(() => { refreshAlerts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [selectedAlert,    setSelectedAlert]    = useState(null);
  const [responseAlert,      setResponseAlert]      = useState(null);  // alert object for modal
  const [isUpdatingResponse, setIsUpdatingResponse] = useState(false);
  const [responseMessage,    setResponseMessage]    = useState('');
  const [responseEta,        setResponseEta]        = useState('');
  const [isSavingResponse,   setIsSavingResponse]   = useState(false);
  const [cantMakeIt,         setCantMakeIt]         = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [selectedSkills,    setSelectedSkills]    = useState([]);
  const [closeAlertId,     setCloseAlertId]     = useState(null);
  const [closeReason,      setCloseReason]      = useState('');
  const [closeNote,        setCloseNote]        = useState('');
  const [isClosingAlert,   setIsClosingAlert]   = useState(false);

  const activeCount = [...myAlerts, ...nearbyAlerts].filter(a => a.status === 'active').length;

  const openMap           = (alert) => setSelectedAlert(alert);
  const closeMap          = ()      => setSelectedAlert(null);
  const openRespondModal  = (alert) => {
    setResponseAlert(alert);
    setIsUpdatingResponse(alert.i_responded ?? false);
    setResponseMessage('');
    setResponseEta('');
    setCantMakeIt(false);
    setSelectedEquipment([]);
    setSelectedSkills([]);
  };
  const closeRespondModal = () => {
    setResponseAlert(null);
    setIsUpdatingResponse(false);
    setResponseMessage('');
    setResponseEta('');
    setCantMakeIt(false);
    setSelectedEquipment([]);
    setSelectedSkills([]);
  };

  const toggleEquipment = (itemId) => {
    setSelectedEquipment(prev =>
      prev.includes(itemId) ? prev.filter(x => x !== itemId) : [...prev, itemId]
    );
  };

  const toggleSkill = (itemId) => {
    setSelectedSkills(prev =>
      prev.includes(itemId) ? prev.filter(x => x !== itemId) : [...prev, itemId]
    );
  };

  const handleResponseSubmit = async (e) => {
    e.preventDefault();
    if (!responseAlert) return;
    setIsSavingResponse(true);
    try {
      await alertsService.respond(
        responseAlert.id,
        responseMessage,
        responseEta,
        selectedEquipment,
        selectedSkills
      );
      addToast('Your response has been recorded!', 'success');
      closeRespondModal();
      refreshAlerts();
    } catch (err) {
      addToast(err.status === 403 ? 'You cannot respond to your own alert.' : 'Could not submit response — please try again.', 'error');
    } finally {
      setIsSavingResponse(false);
    }
  };

  const handleWithdraw = async () => {
    if (!responseAlert) return;
    setIsSavingResponse(true);
    try {
      await alertsService.withdrawResponse(responseAlert.id);
      addToast("Got it — your response has been withdrawn.", 'success');
      closeRespondModal();
      refreshAlerts();
    } catch {
      addToast('Could not withdraw response — please try again.', 'error');
    } finally {
      setIsSavingResponse(false);
    }
  };

  const openCloseModal = (alertId) => {
    setCloseAlertId(alertId);
    setCloseReason('');
    setCloseNote('');
    setIsClosingAlert(false);
  };
  const closeCloseModal = () => {
    setCloseAlertId(null);
    setCloseReason('');
    setCloseNote('');
    setIsClosingAlert(false);
  };

  const handleCloseConfirm = async (e) => {
    e.preventDefault();
    if (!closeAlertId || !closeReason) return;
    const reason = closeReason === 'Other' && closeNote.trim()
      ? `Other: ${closeNote.trim()}`
      : closeReason;
    setIsClosingAlert(true);
    try {
      await deleteAlert(closeAlertId, reason);
      addToast('Alert closed.', 'success');
      closeCloseModal();
    } catch {
      addToast('Could not close alert — please try again.', 'error');
    } finally {
      setIsClosingAlert(false);
    }
  };

  return (
    <div className="alerts-page">
      <div className="container">
        <h1>Alerts</h1>

        <div className="status-bar">
          <h2>Current Alerts</h2>
          <span className="alert-count">{activeCount} Active</span>
        </div>

        {/* ── My Alerts ─────────────────────────────────────────────────── */}
        <h3 className="alerts-section-heading">My Alerts</h3>
        <div className="alerts-list">
          {myAlerts.length === 0 && (
            <p className="alerts-empty">You have no alerts.</p>
          )}
          {myAlerts.map(alert => (
            <div key={alert.id} className={`alert-card ${alert.status}`}>
              <div className="alert-icon">
                {alert.status === 'active'
                  ? <i className="fas fa-exclamation-triangle"></i>
                  : <i className="fas fa-check-circle"></i>}
              </div>
              <div className="alert-content">
                <div className="alert-header">
                  <h3 className="alert-type-link" onClick={() => navigate(`/alerts/${alert.id}`)}>{alert.type}</h3>
                  <span className={`severity ${alert.severity.toLowerCase()}`}>{alert.severity}</span>
                </div>
                <button className="location-button" onClick={() =>
                  alert.property_id ? navigate(`/properties/${alert.property_id}`) : openMap(alert)}>
                  <i className="fas fa-map-marker-alt"></i> {alert.location}
                </button>
                <p className="alert-description">{alert.description}</p>
                {/* Equipment + skill tags */}
                {((alert.neededEquipment ?? []).length > 0 || (alert.neededSkills ?? []).length > 0) && (
                  <div className="alert-needs">
                    {(alert.neededEquipment ?? []).map(item => (
                      <span key={`eq-${item.id}`} className={`need-tag need-eq${item.covered ? ' covered' : ''}`}>
                        {item.covered ? '✓ ' : ''}{item.label}
                      </span>
                    ))}
                    {(alert.neededSkills ?? []).map(item => (
                      <span key={`sk-${item.id}`} className={`need-tag need-sk${item.covered ? ' covered' : ''}`}>
                        {item.covered ? '✓ ' : ''}{item.label}
                      </span>
                    ))}
                  </div>
                )}
                <div className="alert-footer">
                  <div className="alert-times">
                    <span className="alert-time">Issued: {alert.issueDate}</span>
                  </div>
                  <div className="alert-actions">
                    {alert.status === 'active' && (
                      <>
                        <button className="directions-button" onClick={() => getDirections(alert.coordinates, alert.location)}>
                          <span className="material-icons" style={{ fontSize: '1rem', verticalAlign: 'middle' }}>directions_car</span>
                          {' '}Get Directions
                        </button>
                        <button className="dismiss-button" onClick={() => openCloseModal(alert.id)}>
                          <i className="fas fa-times"></i> Close Alert
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Nearby Emergencies ────────────────────────────────────────── */}
        <h3 className="alerts-section-heading nearby">Nearby Emergencies <span className="radius-note">(within 25 mi)</span></h3>
        <div className="alerts-list">
          {nearbyAlerts.length === 0 && (
            <p className="alerts-empty">No active emergencies near your home base.</p>
          )}
          {nearbyAlerts.map(alert => (
            <div key={alert.id} className={`alert-card ${alert.status}`}>
              <div className="alert-icon">
                {alert.status === 'active'
                  ? <i className="fas fa-exclamation-triangle"></i>
                  : <i className="fas fa-check-circle"></i>}
              </div>
              <div className="alert-content">
                <div className="alert-header">
                  <h3 className="alert-type-link" onClick={() => navigate(`/alerts/${alert.id}`)}>{alert.type}</h3>
                  <span className={`severity ${alert.severity.toLowerCase()}`}>{alert.severity}</span>
                  {alert.i_responded && <span className="responded-badge">✓ You're in</span>}
                </div>
                <span className="alert-location-text">{alert.location}</span>
                <p className="alert-description">{alert.description}</p>
                {/* Equipment + skill tags */}
                {((alert.neededEquipment ?? []).length > 0 || (alert.neededSkills ?? []).length > 0) && (
                  <div className="alert-needs">
                    {(alert.neededEquipment ?? []).map(item => (
                      <span key={`eq-${item.id}`} className={`need-tag need-eq${item.covered ? ' covered' : ''}`}>
                        {item.covered ? '✓ ' : ''}{item.label}
                      </span>
                    ))}
                    {(alert.neededSkills ?? []).map(item => (
                      <span key={`sk-${item.id}`} className={`need-tag need-sk${item.covered ? ' covered' : ''}`}>
                        {item.covered ? '✓ ' : ''}{item.label}
                      </span>
                    ))}
                  </div>
                )}
                <div className="alert-footer">
                  <div className="alert-times">
                    <span className="alert-time">Issued: {alert.issueDate}</span>
                  </div>
                  <div className="alert-actions">
                    {alert.status === 'active' && (
                      <>
                        <button className="directions-button" onClick={() => getDirections(alert.coordinates, alert.location)}>
                          <span className="material-icons" style={{ fontSize: '1rem', verticalAlign: 'middle' }}>directions_car</span>
                          {' '}Get Directions
                        </button>
                        <button className="response-button" onClick={() => openRespondModal(alert)}>
                          <i className="fas fa-hand-paper"></i>
                          {alert.i_responded ? ' Update response' : " I can help!"}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {selectedAlert && (
          <div className="map-modal" onClick={closeMap}>
            <div className="map-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="map-modal-header">
                <h3>{selectedAlert.type} - {selectedAlert.location}</h3>
                <button className="close-button" onClick={closeMap}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="map-container">
                <MapContainer
                  center={[selectedAlert.coordinates.lat, selectedAlert.coordinates.lng]}
                  zoom={13}
                  style={{ height: '400px', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  <Marker position={[selectedAlert.coordinates.lat, selectedAlert.coordinates.lng]} />
                </MapContainer>
              </div>
            </div>
          </div>
        )}

        {responseAlert && (
          <div className="response-modal" onClick={closeRespondModal}>
            <div className="response-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="response-modal-header">
                <h3>{isUpdatingResponse ? 'Update your response' : 'Confirm your response'}</h3>
                <button className="close-button" onClick={closeRespondModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <form onSubmit={cantMakeIt ? (e) => { e.preventDefault(); handleWithdraw(); } : handleResponseSubmit} className="response-form">
                <div className="form-group">
                  <label htmlFor="response-eta">
                    Your ETA <span className="label-optional">(optional)</span>
                  </label>
                  <select
                    id="response-eta"
                    className="response-select"
                    value={responseEta}
                    onChange={(e) => setResponseEta(e.target.value)}
                    disabled={cantMakeIt}
                  >
                    <option value="">— Select ETA —</option>
                    <option value="15 min">15 min</option>
                    <option value="30 min">30 min</option>
                    <option value="45 min">45 min</option>
                    <option value="1 hr">1 hr</option>
                    <option value="2 hr">2 hr</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="response-message">
                    Message for the rancher <span className="label-optional">(optional)</span>
                  </label>
                  <textarea
                    id="response-message"
                    className={`response-textarea${cantMakeIt ? ' response-textarea--muted' : ''}`}
                    rows={3}
                    placeholder="e.g. Bringing a livestock trailer, ETA 20 min"
                    value={responseMessage}
                    onChange={(e) => setResponseMessage(e.target.value)}
                    disabled={cantMakeIt}
                  />
                </div>
                {(responseAlert.neededEquipment?.length > 0 || responseAlert.neededSkills?.length > 0) && (
                  <div className="form-group">
                    <label>What can you bring? <span className="label-optional">(optional)</span></label>
                    <div className="response-chips">
                      {(responseAlert.neededEquipment ?? []).map(item => (
                        <button
                          key={`resp-eq-${item.id}`}
                          type="button"
                          className={`response-chip response-chip-eq ${selectedEquipment.includes(item.id) ? 'selected' : ''}`}
                          onClick={() => toggleEquipment(item.id)}
                          disabled={cantMakeIt}
                        >
                          {selectedEquipment.includes(item.id) ? '✓ ' : ''}{item.label}
                        </button>
                      ))}
                      {(responseAlert.neededSkills ?? []).map(item => (
                        <button
                          key={`resp-sk-${item.id}`}
                          type="button"
                          className={`response-chip response-chip-sk ${selectedSkills.includes(item.id) ? 'selected' : ''}`}
                          onClick={() => toggleSkill(item.id)}
                          disabled={cantMakeIt}
                        >
                          {selectedSkills.includes(item.id) ? '✓ ' : ''}{item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {isUpdatingResponse && (
                  <label className="cant-make-it-toggle">
                    <input
                      type="checkbox"
                      checked={cantMakeIt}
                      onChange={(e) => setCantMakeIt(e.target.checked)}
                    />
                    <span>I can't make it after all</span>
                  </label>
                )}
                {cantMakeIt ? (
                  <button type="submit" className="btn-withdraw-confirm" disabled={isSavingResponse}>
                    {isSavingResponse ? 'Withdrawing…' : 'Withdraw my response'}
                  </button>
                ) : (
                  <button type="submit" className="btn-save" disabled={isSavingResponse}>
                    {isSavingResponse ? 'Submitting…' : (isUpdatingResponse ? 'Update response' : "I can help!")}
                  </button>
                )}
              </form>
            </div>
          </div>
        )}

        {closeAlertId && (
          <div className="response-modal" onClick={closeCloseModal}>
            <div className="response-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="response-modal-header">
                <h3>Close alert</h3>
                <button className="close-button" onClick={closeCloseModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <form onSubmit={handleCloseConfirm} className="response-form">
                <div className="form-group">
                  <label>Why are you closing this alert?</label>
                  <div className="response-chips">
                    {['False alarm', 'Alert handled', 'No longer needed', 'Other'].map((reason) => (
                      <button
                        key={reason}
                        type="button"
                        className={`response-chip ${closeReason === reason ? 'selected' : ''}`}
                        onClick={() => setCloseReason(reason)}
                      >
                        {closeReason === reason ? '✓ ' : ''}{reason}
                      </button>
                    ))}
                  </div>
                </div>
                {closeReason === 'Other' && (
                  <div className="form-group">
                    <label htmlFor="close-note">
                      Note <span className="label-optional">(optional)</span>
                    </label>
                    <textarea
                      id="close-note"
                      className="response-textarea"
                      rows={3}
                      placeholder="e.g. Called off, situation resolved by neighbor"
                      value={closeNote}
                      onChange={(e) => setCloseNote(e.target.value)}
                    />
                  </div>
                )}
                <button type="submit" className="btn-save" disabled={!closeReason || isClosingAlert}>
                  {isClosingAlert ? 'Closing…' : 'Close Alert'}
                </button>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default Alerts;
